import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { WatcherLink } from "@common/ADAPT.Common.Model/organisation/watcher-link";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { PeopleQueryUtilities } from "@common/user/people-query-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ObjectivesAuthService } from "@org-common/lib/objectives/objectives-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { SelectionChangedEvent } from "devextreme/ui/list";
import { DxListComponent } from "devextreme-angular";
import { debounceTime, filter, Subject } from "rxjs";
import { WatcherService } from "../watcher.service";

@Component({
    selector: "adapt-select-watcher-list",
    templateUrl: "./select-watcher-list.component.html",
    styleUrl: "./select-watcher-list.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SelectWatcherListComponent extends BaseComponent implements OnInit {
    @Input() public item?: Item;
    @Input() public objective?: Objective;

    public selectablePeople: Person[] = [];
    @ViewChild(DxListComponent) private listComponent?: DxListComponent;

    public componentChanged$ = new Subject<void>();

    public constructor(
        private commonDataService: CommonDataService,
        private teamsService: CommonTeamsService,
        private watcherService: WatcherService,
        private objectivesAuthService: ObjectivesAuthService,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(elementRef);

        rxjsBreezeService.entityTypeChanged(WatcherLink).pipe(
            filter((watcherLink) => (!!watcherLink.itemId && watcherLink.itemId === this.item?.itemId) ||
                (!!watcherLink.objectiveId && watcherLink.objectiveId === this.objective?.objectiveId)),
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.ngOnInit();
            this.listComponent?.instance.unselectAll();
            // clear the search text for the dxList
            this.listComponent?.instance.option("searchValue", "");
            this.componentChanged$.next();
        });
    }

    public async ngOnInit() {
        const excludePersonIds: number[] = [];
        if (this.item) {
            const activeTeamMembers = await this.teamsService.promiseToGetActiveTeamMembers(this.item.board!.team);
            excludePersonIds.push(...activeTeamMembers.map((person) => person.personId));

            if (this.item.assigneeId) {
                excludePersonIds.push(this.item.assigneeId);
            }

            excludePersonIds.push(...this.item.watcherLinks.map((watcherLink) => watcherLink.personId));
        } else if (this.objective) {
            excludePersonIds.push(...this.objective.watcherLinks.map((watcherLink) => watcherLink.personId));
        }

        const objectivePersonFilter = (person: Person) => {
            if (!this.objective) {
                return true;
            }

            // exclude person already included in current watcher view
            return !this.objectivesAuthService.personCanEditObjectivesForTeam(person, this.objective);
        };

        const activePeople = await new PeopleQueryUtilities(this.commonDataService)
            .promiseToGetActivePeople();
        this.selectablePeople = activePeople
            .filter((person) => !excludePersonIds.includes(person.personId))
            .filter((person) => objectivePersonFilter(person))
            .sort((person1, person2) => person1.fullName < person2.fullName ? -1 : 1);
    }

    public handleSelectionChange(e: SelectionChangedEvent) {
        if (e.addedItems?.length) {
            // this is supposed to be just 1 item but event data structure is using an array
            if (this.item) {
                e.addedItems.forEach((person) => this.watcherService.addWatcherLink(person.personId, this.item).subscribe());
            } else if (this.objective) {
                e.addedItems.forEach((person) => this.watcherService.addWatcherLink(person.personId, undefined, this.objective).subscribe());
            }
        }
    }
}
